import { createFileRoute, getRouteApi, useNavigate, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { ScanFace, X } from "lucide-react";
import toast from "react-hot-toast";
import { Button, Checkbox, Spinner } from "@heroui/react";
import { UserAPI } from "@repo/api/user";
import { asSearchString } from "@repo/utils/search-params";
import ErrorPage from "@/pages/-error";
import BridgeHelper from "@/utils/rn-bridge/bridge-helper";
import { NavCode } from "@/utils/rn-bridge/nav-code";
import { faceVerifyFailSearch, getErrorMessage, parseFaceIdVerifyResult } from "./-utils/face-id";

const FACE_VERIFY_TERMS_URL = "https://www.furries.cn/terms/privacy";

export type FaceVerifySearch = {
  recovery_ticket?: string;
};

export const Route = createFileRoute("/help/account_recovery/face_verify")({
  validateSearch: (search: Record<string, unknown>): FaceVerifySearch => ({
    recovery_ticket: asSearchString(search.recovery_ticket),
  }),
  component: FaceVerify,
});

const routeApi = getRouteApi("/help/account_recovery/face_verify");

export default function FaceVerify() {
  const navigate = useNavigate();
  const router = useRouter();
  const { recovery_ticket: recoveryTicket } = routeApi.useSearch();
  const [consentChecked, setConsentChecked] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const handleStart = async () => {
    if (!recoveryTicket) return;
    if (!consentChecked) {
      toast.error("请先同意认证服务协议");
      return;
    }

    setSubmitLoading(true);
    // 人脸识别一旦拉起，后续任何失败都进入失败结果页，要求用户重新开始
    let faceStarted = false;
    const goFail = (message: string) =>
      navigate({ to: "/result/fail", search: faceVerifyFailSearch(message), replace: true });
    try {
      const { data: faceIdData } = await UserAPI.getRecoveryFaceIdToken(recoveryTicket);
      if (!faceIdData) return;

      faceStarted = true;
      const rawResult = await BridgeHelper.sendMessageResult(
        {
          nav_code: NavCode.NAV_FACEID_VERIFY,
          data: faceIdData,
        },
        120_000,
      );

      const { code, result } = parseFaceIdVerifyResult(rawResult);
      if (code !== 1) {
        goFail(result || "认证失败");
        return;
      }

      const { data: verifyData } = await UserAPI.verifyRecoveryFaceId(
        recoveryTicket,
        faceIdData.orderNo,
        faceIdData.nonce,
      );

      navigate({
        to: "/user/account/recovery_phone",
        search: {
          recovery_ticket: recoveryTicket,
          session_code: verifyData.session_code,
        },
        replace: true,
      });
    } catch (err) {
      if (faceStarted) {
        goFail(getErrorMessage(err));
        return;
      }
      toast.error(getErrorMessage(err));
    } finally {
      setSubmitLoading(false);
    }
  };

  const openTerms = () => {
    BridgeHelper.sendMessage({
      nav_code: NavCode.OPEN_PHONE_BROWSER,
      data: { url: FACE_VERIFY_TERMS_URL },
    });
  };

  if (!recoveryTicket) {
    return <ErrorPage error={{ msg: "参数无效", request_id: "FURRIES.PARAMS.INVALID" }} />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-white px-5">
      <div className="relative flex items-center justify-center pt-4">
        <div className="text-lg font-bold">刷脸认证</div>
        <Button
          isIconOnly
          aria-label="关闭"
          variant="ghost"
          className="absolute right-0"
          isDisabled={submitLoading}
          onPress={() => router.history.back()}
        >
          <X className="size-5" />
        </Button>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center">
        <div className="flex size-40 items-center justify-center rounded-full border-2 border-orange-500">
          <ScanFace className="size-24 text-orange-500" strokeWidth={1.25} />
        </div>

        <Checkbox
          name="face-verify-consent"
          className="mt-10 w-full max-w-md"
          isSelected={consentChecked}
          isDisabled={submitLoading}
          onChange={setConsentChecked}
        >
          <Checkbox.Content>
            <Checkbox.Control className="mt-0.5">
              <Checkbox.Indicator />
            </Checkbox.Control>
            <span className="text-sm text-gray-800">
              同意
              <button
                type="button"
                className="text-orange-500"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  openTerms();
                }}
              >
                《认证服务协议》
              </button>
            </span>
          </Checkbox.Content>
        </Checkbox>
        <p className="mt-2 w-full max-w-md pl-7 text-xs leading-relaxed text-gray-400">
          同意认证后，将获取您的摄像头权限用于人脸识别验证身份
        </p>
      </div>

      <div className="flex flex-col gap-3 pb-8">
        <Button
          className="w-full rounded-lg"
          isPending={submitLoading}
          isDisabled={!consentChecked || submitLoading}
          onPress={handleStart}
        >
          {({ isPending }) => (
            <>
              {isPending && <Spinner size="sm" color="current" />}
              开始认证
            </>
          )}
        </Button>
        <Button
          variant="outline"
          className="w-full rounded-lg"
          isDisabled={submitLoading}
          onPress={() => router.history.back()}
        >
          暂不认证
        </Button>
      </div>
    </div>
  );
}
